import { useRef, useState, useEffect, useCallback } from 'react';
import { Article } from '../hooks/useArticles';
import { AudioPlayer } from './AudioPlayer';
import { ShareExtract } from './ShareExtract';
import { TypographyButton } from './TypographyButton';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { useHighlights } from '../hooks/useHighlights';
import { useTextSelection } from '../hooks/useTextSelection';
import { HighlightPopover } from './HighlightPopover';
import { HighlightedContent } from './HighlightedContent';
import { HighlightDetail } from './HighlightDetail';
import { HighlightsSidebar } from './HighlightsSidebar';
import type { DbHighlight } from '../lib/supabase';

interface ArticleReaderProps {
  article: Article;
  onBack: () => void;
  onProgressUpdate?: (progress: number) => void;
  userId?: string;
}

export function ArticleReader({ article, onBack, onProgressUpdate, userId }: ArticleReaderProps) {
  const readerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const articleBodyRef = useRef<HTMLDivElement>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [readingProgress, setReadingProgress] = useState(article.readingProgress || 0);
  const [, setHighlightedSentence] = useState<number | null>(null);
  const lastSavedProgress = useRef(article.readingProgress || 0);

  // Highlighting state
  const [selectedHighlight, setSelectedHighlight] = useState<DbHighlight | null>(null);
  const [showHighlightsSidebar, setShowHighlightsSidebar] = useState(false);

  // Highlighting hooks
  const { selection, clearSelection } = useTextSelection(articleBodyRef);
  const {
    highlights,
    createHighlight,
    updateHighlight,
    deleteHighlight,
    exportAsMarkdown,
  } = useHighlights(article.id, userId);

  // Handle creating a new highlight
  const handleCreateHighlight = async (color: string) => {
    if (!selection) return;
    await createHighlight(
      selection.text,
      selection.startOffset,
      selection.endOffset,
      color
    );
    clearSelection();
  };

  // Handle export - copy markdown to clipboard
  const handleExportHighlights = async () => {
    const markdown = exportAsMarkdown();
    if (markdown) {
      try {
        await navigator.clipboard.writeText(markdown);
        // Could show a toast notification here
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = markdown;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
  };

  // Scroll to highlight when clicked in sidebar
  const handleHighlightClickFromSidebar = (highlight: DbHighlight) => {
    const el = document.querySelector(`[data-highlight-id="${highlight.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setSelectedHighlight(highlight);
  };

  // Swipe right to go back
  useSwipeGesture(readerRef, {
    threshold: 100,
    onSwipeRight: onBack
  });

  // Debounced progress save
  const saveProgress = useCallback((progress: number) => {
    if (onProgressUpdate && Math.abs(progress - lastSavedProgress.current) >= 5) {
      lastSavedProgress.current = progress;
      onProgressUpdate(progress);
    }
  }, [onProgressUpdate]);

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      const clampedProgress = Math.min(100, Math.max(0, progress));

      setReadingProgress(clampedProgress);
      saveProgress(clampedProgress);
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll, { passive: true });
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, [saveProgress]);

  // Restore scroll position based on saved progress
  useEffect(() => {
    if (contentRef.current && article.readingProgress > 0) {
      const element = contentRef.current;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const scrollTop = (article.readingProgress / 100) * scrollHeight;
      element.scrollTop = scrollTop;
    }
  }, [article.readingProgress]);

  const handleSentenceChange = (index: number) => {
    setHighlightedSentence(index);
    // Could scroll to highlighted sentence here if needed
  };

  return (
    <div className="article-reader" ref={readerRef}>
      {/* Reading progress bar */}
      <div className="reading-progress-bar">
        <div
          className="reading-progress-fill"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <header className="reader-header">
        <button className="back-btn" onClick={onBack} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="reader-actions">
          <TypographyButton />
          <button
            className="highlights-toggle"
            onClick={() => setShowHighlightsSidebar(true)}
            title="View highlights"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {highlights.length > 0 && <span>{highlights.length}</span>}
          </button>
          <button
            className="action-btn ai-btn"
            onClick={() => setShowShareModal(true)}
            aria-label="AI features"
            title="AI Summary & Share"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </button>
          <button
            className={`action-btn ${showAudioPlayer ? 'active' : ''}`}
            onClick={() => setShowAudioPlayer(!showAudioPlayer)}
            aria-label="Toggle audio player"
            title="Listen to article"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="reader-content" ref={contentRef}>
        <article className="article-content">
          <h1>{article.title}</h1>
          <div className="article-meta">
            <span>{article.readingTime} min read</span>
            <span>-</span>
            <span>{new Date(article.savedAt).toLocaleDateString()}</span>
          </div>
          {article.tags.length > 0 && (
            <div className="article-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          )}
          <div className="article-body" ref={articleBodyRef}>
            <HighlightedContent
              content={article.content}
              highlights={highlights}
              onHighlightClick={setSelectedHighlight}
            />
          </div>
        </article>
      </div>

      {/* Audio Player */}
      {showAudioPlayer && (
        <AudioPlayer
          content={article.content}
          title={article.title}
          onSentenceChange={handleSentenceChange}
        />
      )}

      {/* Share Extract Modal */}
      {showShareModal && (
        <ShareExtract
          article={article}
          userId={userId}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Highlight Popover - shows when text is selected */}
      {selection && (
        <HighlightPopover
          position={{
            top: selection.rect.top,
            left: selection.rect.left + selection.rect.width / 2,
          }}
          onHighlight={handleCreateHighlight}
          onClose={clearSelection}
        />
      )}

      {/* Highlight Detail Modal - shows when existing highlight is clicked */}
      {selectedHighlight && (
        <HighlightDetail
          highlight={selectedHighlight}
          onUpdate={(updates) => updateHighlight(selectedHighlight.id, updates)}
          onDelete={() => deleteHighlight(selectedHighlight.id)}
          onClose={() => setSelectedHighlight(null)}
        />
      )}

      {/* Highlights Sidebar */}
      {showHighlightsSidebar && (
        <HighlightsSidebar
          highlights={highlights}
          onHighlightClick={handleHighlightClickFromSidebar}
          onExport={handleExportHighlights}
          onClose={() => setShowHighlightsSidebar(false)}
        />
      )}
    </div>
  );
}
